// Copyright (C) 2023 Cochise Ruhulessin
//
// All rights reserved. No warranty, explicit or implicit, provided. In
// no event shall the author(s) be liable for any claim or damages.
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
import { useLocales } from "react-localized"
import { Table } from "../lib"
import { IDocument, PurchaseOrderProduct } from "../models"


interface PurchaseOrderProductProps {
  order: IDocument
}


const PurchaseOrderLines: React.FC<PurchaseOrderProductProps> = ({
  order
}) => {
  const {gettext} = useLocales()
  const moneyFormatter = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: (!!order) ? order.getCurrency() : "EUR"
  })
  return (
    <Table
      columns={[
        {
          key: "_",
          label: gettext("#"),
          width: 15,
          render: (row: PurchaseOrderProduct, index: number) => { return (index + 1).toString() },
        },
        {
          key: "product",
          label: gettext("Product"),
          width: 180,
          render: (row: PurchaseOrderProduct) => { return row.getName() }
        },
        {
          key: "grade",
          label: gettext("Grade"),
          width: 60,
          render: (row: PurchaseOrderProduct) => { return row.getGrade() || "—"}
        },
        {
          key: "quantity",
          label: gettext("Quantity"),
          width: 90,
          render: (row: PurchaseOrderProduct) => { return row.getQuantity().toString() },
          summarize: (rows: PurchaseOrderProduct[]) => {
            return rows.map((p) => p.getQuantity()).reduce((a, b) => a + b, 0).toString()
          }
        },
        {
          key: "price",
          label: gettext("Price"),
          width: 90,
          render: (row: PurchaseOrderProduct) => {
            return moneyFormatter.format(row.getPrice())
          }
        },
        {
          key: "total",
          label: gettext("Total"),
          width: 90,
          render: (row: PurchaseOrderProduct) => { return moneyFormatter.format(row.getPrice() * row.getQuantity()) },
          summarize: (rows: PurchaseOrderProduct[]) => {
            return moneyFormatter.format(
              rows.map((p) => p.getQuantity() * p.getPrice()).reduce((a, b) => a + b, 0)
            )
          }
        },
      ]}
      items={(!!order) ? order.getProducts() : []}
    />
  )
}


export {PurchaseOrderLines}